import api from './api';

const resource = 'Platform/';

// GET Platform/Finance's from/to are the same inclusive UTC-range convention
// as Connect's date-ranged reads (see connectApi.js's financeQuery comment) —
// callers pass ISO 8601 instants, either bound may be omitted.
const financeQuery = (from, to) => {
  const parts = [];
  if (from) parts.push('from=' + encodeURIComponent(from));
  if (to) parts.push('to=' + encodeURIComponent(to));
  return parts.length ? '?' + parts.join('&') : '';
};

export const getFinance = async (from, to) => {
  return await api.get(resource + 'Finance' + financeQuery(from, to));
};

// GET Platform/Finance/Trend?months=N — the last N calendar months' totals,
// oldest first, for the admin Overview's trend chart (owner, 2026-09-24).
export const getTrend = async (months = 6) => {
  return await api.get(resource + 'Finance/Trend?months=' + encodeURIComponent(months));
};

export const getHealth = async () => {
  return await api.get(resource + 'Health');
};

// PlatformController answers both endpoints bare — `Ok(dto)`, no Response<T>
// envelope — the same convention connectApi.js's `listOf`/`toConnectStatus`
// read off Connect's endpoints. `GET Platform/Finance` returns
// PlatformFinanceResultDto: `{ rows: [...], totals: {...} }` directly on
// `res.data`, not under a second `.data`.

// Unlike Connect's finance DTOs (integer cents, converted in connectApi.js's
// `dollars`), PlatformFinanceRowDto/TotalsDto's money fields are C# `decimal`
// and already dollars by the time they leave the API:
// PlatformFinanceService.AppendConnectRowsAsync converts Connect's cent longs
// with its own `Dollars(cents) => cents / 100m` helper *before* assigning
// them (e.g. `Dollars(agg.grossamount)`), and GetHealthAsync does the same
// with `/ 100m` for averageDelta/feesNotCollected. Legacy rows pass Order's
// decimal columns straight through, which were never cents to begin with.
// So nothing here divides by 100 — a second conversion would be a second,
// wrong one, quietly shrinking every number on the page by 100x.
const numberOrNull = (v) => (typeof v === 'number' ? v : null);
const numberOrZero = (v) => (typeof v === 'number' ? v : 0);
const boolOrNull = (v) => (typeof v === 'boolean' ? v : null);

/**
 * GET Platform/Finance's per-company rows, PlatformFinanceRowDto[].
 *
 * `stripeFee` and `failedCount` stay null, not 0, when the API sends null:
 * a Connect row's real Stripe fee is unknown until its webhook lands
 * (PlatformFinanceProjection.ConnectRow's `actualstripefee` doc), and a
 * legacy row has no failure concept at all — PaymentStatusEnum is
 * NotPaid/Paid only, so legacy rows carry `failedcount: null` rather than a
 * fabricated zero. Both facts must survive to the screen distinguishable
 * from "we counted zero".
 */
export const toPlatformRows = (res) => {
  const rows = res?.data?.rows;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    companyId: row.companyid,
    companyName: row.companyname || row.companyid,
    model: row.model,
    connectStatus: numberOrNull(row.connectstatus),
    chargesEnabled: boolOrNull(row.chargesenabled),
    payoutsEnabled: boolOrNull(row.payoutsenabled),
    feePayerVerified: boolOrNull(row.feepayerverified),
    grossAmount: numberOrZero(row.grossamount),
    ourShare: numberOrZero(row.ourshare),
    restaurantNet: numberOrZero(row.restaurantnet),
    stripeFee: numberOrNull(row.stripefee),
    stripeFeePaidByRestaurant: !!row.stripefeepaidbyrestaurant,
    paymentCount: numberOrZero(row.paymentcount),
    failedCount: numberOrNull(row.failedcount),
    feesNotCollected: numberOrZero(row.feesnotcollected),
    averageOrder: numberOrZero(row.averageorder),
    error: row.error || null,
  }));
};

/**
 * GET Platform/Finance's totals, PlatformFinanceTotalsDto. `complete` is
 * false when any row could not be computed
 * (`PlatformFinanceProjection.Totals`: `list.All(r => r.error is null)`) —
 * the page reads this to mark the totals row as partial instead of
 * presenting a silently-short sum as final.
 */
export const toPlatformTotals = (res) => {
  const totals = res?.data?.totals;
  if (!totals || typeof totals !== 'object') return null;
  return {
    grossAmount: numberOrZero(totals.grossamount),
    ourShare: numberOrZero(totals.ourshare),
    restaurantNet: numberOrZero(totals.restaurantnet),
    paymentCount: numberOrZero(totals.paymentcount),
    failedCount: numberOrZero(totals.failedcount),
    complete: totals.complete !== false,
  };
};

/**
 * GET Platform/Health, PlatformHealthDto. `readable: false` means the read
 * itself failed (Stripe/DB blew up) — callers must check it before trusting
 * `hasHardSignal`, which `PlatformHealthProjection.Unreadable` forces to
 * false rather than leaving true or inventing a green state. Unlike the
 * finance DTO, this one's field names already arrive camelCase off the wire
 * (Newtonsoft serializes the C# property names as declared, with no naming
 * policy configured), so they're read as-is rather than lowercased.
 */
// PlatformFinanceTrendDto: `{ points: [{ month: 'yyyy-MM', grossamount, ourshare,
// restaurantnet, paymentcount, complete }] }` — decimals already in dollars,
// like the finance rows. Anything but an array of points reads as empty.
export const toPlatformTrend = (res) => {
  const points = Array.isArray(res?.data?.points) ? res.data.points : [];
  return points.map((p) => ({
    month: p.month || '',
    grossAmount: numberOrZero(p.grossamount),
    ourShare: numberOrZero(p.ourshare),
    restaurantNet: numberOrZero(p.restaurantnet),
    paymentCount: numberOrZero(p.paymentcount),
    complete: p.complete !== false,
  }));
};

export const toPlatformHealth = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  return {
    readable: dto.readable !== false,
    reason: dto.reason || null,
    hasHardSignal: !!dto.hasHardSignal,
    hardSignals: Array.isArray(dto.hardSignals) ? dto.hardSignals : [],
    chargesDisabledCount: numberOrZero(dto.chargesDisabledCount),
    payoutsDisabledCount: numberOrZero(dto.payoutsDisabledCount),
    webhookAgeMinutes: numberOrZero(dto.webhookAgeMinutes),
    averageDelta: numberOrZero(dto.averageDelta),
    feesNotCollected: numberOrZero(dto.feesNotCollected),
  };
};

const platformApi = {
  getFinance,
  getHealth,
  toPlatformRows,
  toPlatformTotals,
  toPlatformHealth,
  getTrend,
  toPlatformTrend,
};

export default platformApi;
